// @LTG-ID: contact-step-business
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: source `Contact.html` lines 783-835 (v4)
// @CHANGED: 2026-05 session_006 (CHG-0083-M-A1)
// @OWNER: web
//
// CHG-0083-M-A1 — Step 2: Business name + State.
//
// Greeting: "Thanks, {firstName}. Tell us about the business."
// Business name input + State select (50 US states + DC, alphabetical).
// Custom-styled <select> with inline chevron SVG background (see CSS).
// Back button enabled; Continue gated on validateBusinessName + validateState.

import { useState } from "react";
import {
    validateBusinessName,
    validateState,
    US_STATES,
} from "./validators";

export const StepBusiness = ({ data, setData, onNext, onBack }) => {
    const [touched, setTouched] = useState({});
    const bizCheck = validateBusinessName(data.businessName);
    const stateCheck = validateState(data.state);
    const valid = bizCheck.ok && stateCheck.ok;
    const first = (data.firstName || "").trim();

    const tryNext = () => {
        setTouched({ businessName: true, state: true });
        if (valid) onNext();
    };

    const showBizErr = touched.businessName && !bizCheck.ok;
    const showStateErr = touched.state && !stateCheck.ok;

    return (
        <section
            className="ltgc-step"
            data-testid="contact-step-business"
            aria-labelledby="ltgc-step-business-heading"
        >
            <span className="ltgc-step-greeting">Step 2 · The business</span>
            <h2 id="ltgc-step-business-heading">
                Thanks, {first}. <em>Tell us about the business</em>.
            </h2>
            <p className="ltgc-sub">
                Just the name and the state. The state helps us route to the
                right tax desk; the business name helps us reference you
                correctly.
            </p>

            <div className="ltgc-field">
                <label
                    className="ltgc-field-label"
                    htmlFor="ltgc-input-business-name"
                >
                    Business name
                </label>
                <input
                    id="ltgc-input-business-name"
                    className={
                        "ltgc-field-input " + (showBizErr ? "is-error" : "")
                    }
                    type="text"
                    autoFocus
                    placeholder="Carolina Plumbing Co."
                    autoComplete="organization"
                    value={data.businessName}
                    aria-required="true"
                    aria-invalid={showBizErr ? "true" : "false"}
                    aria-describedby={
                        showBizErr
                            ? "ltgc-input-business-name-error"
                            : undefined
                    }
                    data-testid="contact-input-business-name"
                    onChange={(e) =>
                        setData({ ...data, businessName: e.target.value })
                    }
                    onBlur={() =>
                        setTouched((t) => ({ ...t, businessName: true }))
                    }
                    onKeyDown={(e) => {
                        if (e.key === "Enter") tryNext();
                    }}
                />
                {showBizErr && (
                    <span
                        id="ltgc-input-business-name-error"
                        className="ltgc-field-error"
                        role="alert"
                        data-testid="contact-error-business-name"
                    >
                        {bizCheck.msg}
                    </span>
                )}
            </div>

            <div className="ltgc-field">
                <label
                    className="ltgc-field-label"
                    htmlFor="ltgc-input-state"
                >
                    State
                </label>
                <select
                    id="ltgc-input-state"
                    className={
                        "ltgc-field-input ltgc-select-input " +
                        (showStateErr ? "is-error" : "")
                    }
                    value={data.state}
                    aria-required="true"
                    aria-invalid={showStateErr ? "true" : "false"}
                    aria-describedby={
                        showStateErr ? "ltgc-input-state-error" : undefined
                    }
                    data-testid="contact-input-state"
                    onChange={(e) =>
                        setData({ ...data, state: e.target.value })
                    }
                    onBlur={() =>
                        setTouched((t) => ({ ...t, state: true }))
                    }
                >
                    <option value="">Select your state</option>
                    {US_STATES.map(([abbr, name]) => (
                        <option key={abbr} value={abbr}>
                            {name}
                        </option>
                    ))}
                </select>
                {showStateErr && (
                    <span
                        id="ltgc-input-state-error"
                        className="ltgc-field-error"
                        role="alert"
                        data-testid="contact-error-state"
                    >
                        {stateCheck.msg}
                    </span>
                )}
            </div>

            <div className="ltgc-step-nav">
                <button
                    type="button"
                    className="ltgc-btn ltgc-btn-ghost"
                    onClick={onBack}
                    data-testid="contact-step-business-back"
                >
                    ← Back
                </button>
                <button
                    type="button"
                    className="ltgc-btn ltgc-btn-primary"
                    disabled={!valid}
                    onClick={tryNext}
                    data-testid="contact-step-business-continue"
                >
                    Continue <span className="ltgc-arrow">→</span>
                </button>
            </div>
        </section>
    );
};

export default StepBusiness;
